import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateMappingFormDTO,
  UpdateMappingFormDTO,
} from "@/modules/jd-mapping/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { jdMappingApi } from "./client";

export const jdMappingKeys = {
  all: ["jd-mapping"] as const,
  positions: (q: Partial<OffsetPageQueryDTO>) =>
    ["jd-mapping", "positions", q] as const,
  history: (positionId: string) =>
    ["jd-mapping", "history", positionId] as const,
  current: (positionId: string) =>
    ["jd-mapping", "current", positionId] as const,
};

export function usePositionsWithJDQuery(
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: jdMappingKeys.positions(q),
    queryFn: () => jdMappingApi.listPositions(q),
  });
}

export function useMappingHistoryQuery(positionId: string) {
  return useQuery({
    queryKey: jdMappingKeys.history(positionId),
    queryFn: () => jdMappingApi.getMappingHistory(positionId),
    enabled: !!positionId,
  });
}

export function useCurrentMappingQuery(positionId: string) {
  return useQuery({
    queryKey: jdMappingKeys.current(positionId),
    queryFn: () => jdMappingApi.getCurrentMapping(positionId),
    enabled: !!positionId,
  });
}

export function useCreateMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMappingFormDTO) => jdMappingApi.create(input),
    onSuccess: () => {
      toast.success("ຜູກ JD ກັບຕໍາແໜ່ງສຳເລັດ");
      qc.invalidateQueries({ queryKey: jdMappingKeys.all });
    },
    onError: () => toast.error("ຜູກ JD ກັບຕໍາແໜ່ງລົ້ມເຫຼວ"),
  });
}

export function useUpdateMapping(positionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMappingFormDTO) =>
      jdMappingApi.update(positionId, input),
    onSuccess: () => {
      toast.success("ປ່ຽນ JD ສຳເລັດ");
      qc.invalidateQueries({ queryKey: jdMappingKeys.all });
      qc.invalidateQueries({ queryKey: jdMappingKeys.history(positionId) });
      qc.invalidateQueries({ queryKey: jdMappingKeys.current(positionId) });
    },
    onError: () => toast.error("ປ່ຽນ JD ລົ້ມເຫຼວ"),
  });
}

export function useCancelMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      positionId,
      notes,
    }: {
      positionId: string;
      notes?: string;
    }) => jdMappingApi.cancel(positionId, notes),
    onSuccess: (_, { positionId }) => {
      toast.success("ຍົກເລີກການຜູກ JD ສຳເລັດ");
      qc.invalidateQueries({ queryKey: jdMappingKeys.all });
      qc.invalidateQueries({ queryKey: jdMappingKeys.history(positionId) });
      qc.invalidateQueries({ queryKey: jdMappingKeys.current(positionId) });
    },
    onError: () => toast.error("ຍົກເລີກການຜູກ JD ລົ້ມເຫຼວ"),
  });
}
